import React, { useState, useEffect, useMemo } from "react";
import { Select, InputNumber, Spin, Switch } from "antd";
import {
  getUnassignAccountTypeApi,
  getUnassignApprovedAccountTypeApi,
  GetMixCommissionChainApi,
  SaveTempPlanApi,
  UpdateIbRequestApi,
  AddPlanToIBUserApi,
} from "../../api/request/ibapproved";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import LoadableButton from "../../components/buttons/LoadableButton";

const { Option } = Select;

const IbConfig = () => {
  const { id, type: approveType, planId } = useParams();
  const navigate = useNavigate();
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [chainData, setChainData] = useState(null);
  const [newIBCommission, setNewIBCommission] = useState(0);
  const [selfCommission, setSelfCommission] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const getUnassignAccountType = async () => {
    try {
      if (approveType === "pending") {
        const { data } = await getUnassignAccountTypeApi(
          id,
          planId != 0 && planId,
        );
        setAccountTypes(data?.data);
      } else {
        const { data } = await getUnassignApprovedAccountTypeApi(
          id,
          planId != 0 && planId,
        );
        setAccountTypes(data?.data);
      }
    } catch (error) {
      console.error("Error fetching unassigned account types:", error);
    }
  };

  useEffect(() => {
    getUnassignAccountType();
  }, [id]);

  useEffect(() => {
    if (
      planId &&
      planId != "0" &&
      planId !== "undefined" &&
      accountTypes.length > 0
    ) {
      const matchingAccountType = accountTypes.find(
        (type) => type.id === planId,
      );
      if (matchingAccountType) {
        setSelectedAccountType(planId);
      }
    }
  }, [planId, accountTypes]);

  const selectedAccountTypeObj = useMemo(() => {
    return accountTypes?.find((type) => type.id === selectedAccountType);
  }, [accountTypes, selectedAccountType]);

  const [isChainLoading, setIsChainLoading] = useState(false);

  const getCommissionChain = async (accountTypeId) => {
    try {
      setIsChainLoading(true);
      const { data } = await GetMixCommissionChainApi(id, accountTypeId);
      if (data?.success) {
        setChainData(data?.data);
      } else {
        setChainData(null);
      }
    } catch (error) {
      console.error("Error fetching commission chain:", error);
      setChainData(null);
    } finally {
      setIsChainLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccountType) {
      getCommissionChain(selectedAccountType);
    } else {
      setChainData(null);
      setNewIBCommission(0);
      setSelfCommission(false);
    }
  }, [selectedAccountType]);

  useEffect(() => {
    if (chainData) {
      setNewIBCommission(chainData.ownCommission || 0);
      setSelfCommission(chainData.selfCommission ?? false);
    }
  }, [chainData]);

  const maxCommission =
    chainData?.maxCommission || selectedAccountTypeObj?.maxCommission || 0;

  const allocatedCommission = useMemo(() => {
    if (!chainData?.chain) return 0;
    return chainData.chain.reduce((sum, ib) => sum + (ib.commission || 0), 0);
  }, [chainData]);

  const remainingForNewIB = useMemo(() => {
    return maxCommission - allocatedCommission;
  }, [maxCommission, allocatedCommission]);

  const remainingAfterNewIB = useMemo(() => {
    return remainingForNewIB - (newIBCommission || 0);
  }, [remainingForNewIB, newIBCommission]);

  const validationErrors = useMemo(() => {
    if (!chainData) return {};
    const errors = {};
    const total = maxCommission;

    chainData.chain.forEach((ib) => {
      if (ib.commission < 0) {
        errors[`chain_${ib.ibUserId}`] = "Cannot be negative";
      }
    });

    if (newIBCommission < 0) {
      errors.newIB = "Cannot be negative";
    }

    const grandTotal = allocatedCommission + (newIBCommission || 0);
    if (grandTotal > total) {
      errors.total = `Total allocated ($${grandTotal.toFixed(2)}) exceeds maximum ($${total})`;
    }

    return errors;
  }, [chainData, allocatedCommission, newIBCommission]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleChainCommissionChange = (ibUserId, newValue) => {
    setChainData((prev) => ({
      ...prev,
      chain: prev.chain.map((ib) =>
        ib.ibUserId === ibUserId ? { ...ib, commission: newValue || 0 } : ib,
      ),
    }));
  };

  const handleNewIBCommissionChange = (value) => {
    setNewIBCommission(value || 0);
  };

  const handleSave = async () => {
    if (hasErrors || !selectedAccountType) return;

    try {
      setIsSaving(true);
      const payload = {
        userId: id,
        planId: selectedAccountType,
        mode: 3,
        commission: newIBCommission,
        selfCommission,
        mixCommission: {
          ownCommission: newIBCommission,
          chain: chainData.chain.map((ib) => ({
            ibUserId: ib.ibUserId,
            commission: ib.commission,
          })),
        },
      };

      if (planId && planId !== "0") {
        payload.id = planId;
      }

      let response;
      if (approveType === "pending") {
        response = await SaveTempPlanApi({ ...payload, mode: 4 });
      } else if (planId && planId !== "0") {
        response = await UpdateIbRequestApi(payload);
      } else {
        response = await AddPlanToIBUserApi([payload]);
      }

      const { data } = response;
      if (data?.success) {
        toast.success(data?.message || "Configuration saved successfully");
        await getCommissionChain(selectedAccountType);
      } else {
        toast.error(data?.message || "Failed to save configuration");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message || "Failed to save configuration",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const usedPercent = maxCommission
    ? ((allocatedCommission + (newIBCommission || 0)) / maxCommission) * 100
    : 0;

  return (
    <div className="space-y-5">
      {/* Account Type Selection */}
      <div className="border-2 border-light rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[#2F2B3D] text-lg font-semibold m-0">
            Account Type Selection
          </p>
          <button
            type="button"
            disabled={!selectedAccountType}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            onClick={() =>
              navigate(
                `/ib_overrides/${approveType}/${id}/${selectedAccountType}`,
              )
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              className="h-4 w-4"
            >
              <path d="M4 7h10M4 12h16M4 17h8" />
            </svg>
            Overrides
          </button>
        </div>
        <div className="input-white">
          <label className="text-description text-[#2F2B3DCC] font-medium">
            Account Type
          </label>
          <Select
            placeholder="Select Account Type"
            value={selectedAccountType}
            onChange={setSelectedAccountType}
            className="w-full mt-1"
            size="large"
            showSearch
            optionFilterProp="label"
            disabled={planId != 0}
          >
            {accountTypes.map((type) => (
              <Option
                key={type.id}
                value={type.id}
                label={`${type.name} ${type.groupName}`}
              >
                <div className="flex items-center justify-between">
                  <span>
                    {type.name} ({type.groupName})
                  </span>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded ${type?.parentCommissionType === "static" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}
                  >
                    {type?.parentCommissionType}
                  </span>
                </div>
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {isChainLoading && (
        <div className="flex justify-center flex-col items-center py-16">
          <Spin size="large" />
          <p className="primary text-center mt-2">
            Loading commission chain...
          </p>
        </div>
      )}

      {!isChainLoading && chainData && (
        <>
          {/* Summary Bar */}
          <div className="border-2 border-light rounded-xl p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#2F2B3DCC] font-medium">
                  Max Commission:
                </span>
                <span className="primary text-lg font-semibold">
                  ${maxCommission.toFixed(2)}
                </span>
                <span className="text-xs text-gray-400">
                  ({selectedAccountTypeObj?.name})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#2F2B3DCC] font-medium">
                  Allocated:
                </span>
                <span className="text-lg font-semibold text-orange-600">
                  ${allocatedCommission.toFixed(2)}
                </span>
                <span className="text-xs text-gray-400">
                  ({chainData.chain.length} IBs)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#2F2B3DCC] font-medium">
                  New IB:
                </span>
                <span className="text-lg font-semibold text-blue-600">
                  ${(newIBCommission || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#2F2B3DCC] font-medium">
                  Remaining:
                </span>
                <span
                  className={`text-lg font-semibold ${remainingAfterNewIB < 0 ? "text-red-600" : "text-green-600"}`}
                >
                  ${remainingAfterNewIB.toFixed(2)}
                </span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${usedPercent > 100 ? "bg-red-500" : "bg-primary"}`}
                style={{ width: `${Math.min(usedPercent, 100)}%` }}
              />
            </div>
            {validationErrors.total && (
              <p className="text-red-500 text-sm mt-2">
                {validationErrors.total}
              </p>
            )}
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-5 gap-5 items-start">
            {/* Left: Commission Chain */}
            <div className="lg:col-span-3 border-2 border-light rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[#2F2B3D] text-lg font-semibold">
                  Commission Chain
                </p>
                <span className="text-xs text-gray-400">
                  {chainData.chain.length} level(s)
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Edit upper-link commissions to adjust the budget for the new IB.
              </p>

              <div className="max-h-[calc(100vh-320px)] overflow-y-auto pr-1 space-y-2">
                {chainData.chain.map((ib) => (
                  <div
                    key={ib.ibUserId}
                    className="flex items-center gap-3 px-3 py-2.5 border rounded-lg bg-white hover:border-blue-200 transition-colors"
                  >
                    <div className="bg-primary text-white px-2.5 py-0.5 rounded text-xs font-medium min-w-[65px] text-center">
                      Level {ib.level}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2F2B3D] text-sm truncate">
                        {ib.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {ib.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-gray-400 text-sm">$</span>
                      <InputNumber
                        value={ib.commission}
                        onChange={(val) =>
                          handleChainCommissionChange(ib.ibUserId, val)
                        }
                        min={0}
                        step={0.01}
                        precision={2}
                        className="w-24"
                        placeholder="0.00"
                        size="small"
                        status={
                          validationErrors[`chain_${ib.ibUserId}`]
                            ? "error"
                            : ""
                        }
                      />
                      <span className="text-gray-400 text-xs">/ lot</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: New IB Assignment (sticky) */}
            <div className="lg:col-span-2 lg:sticky lg:top-5 lg:self-start space-y-5">
              <div className="border-2 border-light rounded-xl p-5">
                <p className="text-[#2F2B3D] text-lg font-semibold mb-4">
                  Assign to New IB
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="text-description text-[#2F2B3DCC] font-medium">
                      Commission Amount
                    </label>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-gray-500 text-lg">$</span>
                      <InputNumber
                        value={newIBCommission}
                        onChange={handleNewIBCommissionChange}
                        min={0}
                        step={0.01}
                        precision={2}
                        className="w-full"
                        placeholder="0.00"
                        size="large"
                        status={validationErrors.newIB ? "error" : ""}
                      />
                      <span className="text-gray-400 text-sm whitespace-nowrap">
                        per lot
                      </span>
                    </div>
                    {validationErrors.newIB && (
                      <p className="text-red-500 text-xs mt-1">
                        {validationErrors.newIB}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between border border-light rounded-lg p-3">
                    <p className="font-semibold text-sm text-[#2F2B3D]">
                      Self Commission
                    </p>
                    <Switch
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      checked={selfCommission}
                      onChange={setSelfCommission}
                    />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        Available before assignment
                      </span>
                      <span className="font-medium text-[#2F2B3D]">
                        ${remainingForNewIB.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Assigning to new IB</span>
                      <span className="font-medium text-blue-600">
                        - ${(newIBCommission || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="border-t pt-2 flex justify-between text-sm">
                      <span className="text-gray-500 font-medium">
                        Remaining after
                      </span>
                      <span
                        className={`font-semibold ${remainingAfterNewIB < 0 ? "text-red-600" : "text-green-600"}`}
                      >
                        ${remainingAfterNewIB.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <LoadableButton
                    className="bg-primary text-sm text-white font-semibold rounded-lg w-full py-2.5 themeHover duration-500"
                    onClick={handleSave}
                    isLoading={isSaving}
                    disabled={isSaving || hasErrors || !selectedAccountType}
                    loadingLable="Saving..."
                    lable="Save Configuration"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IbConfig;
